import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import api from '../services/api';
import Cookies from 'js-cookie';

const UploadContext = createContext();

export const UploadProvider = ({ children, onDocumentCompleted }) => {
    const [uploads, setUploads] = useState([]); // [{id, file, status, error, tempId}]
    const token = Cookies.get('token');
    const canceledUploads = useRef(new Set());

    // Adiciona um novo upload à lista (pode ser temporário)
    const addUpload = useCallback((file, id, isTemp = false) => {
        setUploads(prev => [
            ...prev,
            {
                id,
                file,
                status: 'pending',
                error: null,
                temp: isTemp
            }
        ]);
    }, []);

    // Atualiza o status de um upload
    const updateUploadStatus = useCallback((uploadId, status, error = null) => {
        setUploads(prev => prev.map(upload =>
            upload.id === uploadId ? { ...upload, status, error } : upload
        ));
    }, []);

    // Atualiza o id temporário para o id real
    const updateUploadId = useCallback((tempId, realId) => {
        setUploads(prev => prev.map(upload =>
            upload.id === tempId ? { ...upload, id: realId, temp: false } : upload
        ));
    }, []);

    // Remove upload da lista
    const removeUpload = useCallback((uploadId) => {
        setUploads(prev => prev.filter(upload => upload.id !== uploadId));
    }, []);

    // Polling do status do documento
    const pollStatus = useCallback((documentId) => {
        let interval = setInterval(async () => {
            try {
                if (canceledUploads.current.has(documentId)) {
                    clearInterval(interval);
                    return;
                }

                const response = await api.get(`/api/documents/${documentId}/status`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                const status = response.data.status;
                updateUploadStatus(documentId, status);

                if (status === 'completed' || status === 'failed' || status === 'canceled') {
                    clearInterval(interval);
                    
                    if (status === 'completed') {
                        // Chama onDocumentCompleted para atualizar a tabela
                        if (typeof onDocumentCompleted === 'function') {
                            onDocumentCompleted();
                        }
                        // Aguarda um momento para garantir que a tabela foi atualizada
                        setTimeout(() => {
                            removeUpload(documentId);
                        }, 2000);
                    } else {
                        // Para outros status, remove imediatamente
                        removeUpload(documentId);
                    }
                }
            } catch (err) {
                updateUploadStatus(documentId, 'failed', 'Erro ao consultar status');
                clearInterval(interval);
                setTimeout(() => removeUpload(documentId), 2000);
            }
        }, 2000);
    }, [token, updateUploadStatus, removeUpload, onDocumentCompleted]);

    // Inicia o upload do arquivo
    const startUpload = useCallback(async (file) => {
        const tempId = Date.now().toString() + '-' + file.name;
        addUpload(file, tempId, true);

        try {
            if (canceledUploads.current.has(tempId)) {
                updateUploadStatus(tempId, 'canceled');
                removeUpload(tempId);
                return;
            }

            const formData = new FormData();
            formData.append('file', file);
            
            const response = await api.post('/api/documents', formData, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            if (canceledUploads.current.has(tempId)) {
                updateUploadStatus(tempId, 'canceled');
                removeUpload(tempId);
                return;
            }

            const document = response.data.document || response.data;
            updateUploadId(tempId, document.id);
            pollStatus(document.id);
        } catch (error) {
            if (!canceledUploads.current.has(tempId)) {
                updateUploadStatus(tempId, 'failed', error.message || 'Erro ao enviar arquivo');
                setTimeout(() => removeUpload(tempId), 5000);
            }
        }
    }, [token, addUpload, updateUploadStatus, updateUploadId, pollStatus, removeUpload]);

    // Função para cancelar upload
    const cancelUpload = useCallback(async (uploadId) => {
        try {
            // Primeiro atualiza o status localmente para feedback imediato
            canceledUploads.current.add(uploadId);
            updateUploadStatus(uploadId, 'canceled');
            
            // Encontra o upload para pegar o ID real do documento
            const upload = uploads.find(u => u.id === uploadId);
            if (!upload) {
                console.error('Upload não encontrado:', uploadId);
                return;
            }

            // Chama a API de cancelamento
            const response = await api.post(`/api/documents/${uploadId}/cancel`, {}, {
                headers: { 'Authorization': `Bearer ${token}` }
            });

            // Se chegou aqui, o cancelamento foi bem sucedido
            console.log('Upload cancelado com sucesso:', response.data.message);
            
            // Remove o upload da lista após um pequeno delay para o usuário ver o status
            setTimeout(() => {
                removeUpload(uploadId);
            }, 2000);
        } catch (error) {
            console.error('Erro ao cancelar upload:', error);
            
            // Verifica o tipo de erro
            if (error.response) {
                const { status, data } = error.response;
                
                // Se for erro de permissão ou upload não encontrado
                if (status === 403 || status === 404) {
                    console.error('Erro de permissão ou upload não encontrado:', data.error);
                }
                // Se for erro de status inválido
                else if (status === 400) {
                    console.error('Upload não pode ser cancelado:', data.error);
                }
                // Se for erro interno do servidor
                else if (status === 500) {
                    console.error('Erro interno ao cancelar upload:', data.error);
                }
            }

            // Mantém o status como 'canceled' mesmo em caso de erro
            updateUploadStatus(uploadId, 'canceled');
            
            // Remove o upload da lista após um pequeno delay
            setTimeout(() => {
                removeUpload(uploadId);
            }, 2000);
        }
    }, [token, updateUploadStatus, removeUpload, uploads]);

    // Upload múltiplo
    const startMultipleUploads = useCallback(async (files) => {
        Array.from(files).forEach(file => {
            startUpload(file);
        });
    }, [startUpload]);

    return (
        <UploadContext.Provider value={{
            uploads,
            startUpload,
            startMultipleUploads,
            removeUpload,
            updateUploadStatus,
            cancelUpload
        }}>
            {children}
        </UploadContext.Provider>
    );
};

export const useUpload = () => {
    const context = useContext(UploadContext);
    if (!context) {
        throw new Error('useUpload deve ser usado dentro de um UploadProvider');
    }
    return context;
}; 